import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useRailScroll } from '@hooks/useRailScroll';
import { RowGrid, RowList, RowRoot, ScrollButton } from './FooterRow.styled';

interface FooterRowProps {
  readonly grid: boolean;
  readonly label: string;
  readonly children: ReactNode;
}

const FooterRow: React.FC<FooterRowProps> = ({ grid, label, children }) => {
  const intl = useIntl();
  const { railRef, measure, canPrevious, canNext, previous, next, previousButtonRef, nextButtonRef } =
    useRailScroll({ sidewaysWheel: true });

  if (grid) {
    return (
      <RowGrid aria-label={label} data-testid="apps-footer__grid">
        {children}
      </RowGrid>
    );
  }

  return (
    <RowRoot>
      {canPrevious && (
        <ScrollButton
          ref={previousButtonRef}
          type="button"
          $side="start"
          onClick={previous}
          aria-label={intl.formatMessage({ id: 'appsFooter.previous', defaultMessage: 'Previous' })}
          data-testid="apps-footer__previous"
        >
          <MdChevronLeft size={20} aria-hidden="true" />
        </ScrollButton>
      )}
      <RowList ref={railRef} onScroll={measure} aria-label={label} data-testid="apps-footer__row">
        {children}
      </RowList>
      {canNext && (
        <ScrollButton
          ref={nextButtonRef}
          type="button"
          $side="end"
          onClick={next}
          aria-label={intl.formatMessage({ id: 'appsFooter.next', defaultMessage: 'Next' })}
          data-testid="apps-footer__next"
        >
          <MdChevronRight size={20} aria-hidden="true" />
        </ScrollButton>
      )}
    </RowRoot>
  );
};

export default FooterRow;
